import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Visit } from '../types/visit';
import { format } from 'date-fns';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { useInventory } from '../../../features/inventory/hooks/useInventory';
import { useState, useEffect } from 'react';
import { useUser } from '../../../features/settings/hooks/useUser';
import { getVisitPrescriptionChanges } from '../api/prescriptions';
import { PrescriptionChange } from '../types/prescription';
import { InventoryItem } from '../../../features/inventory/types/inventory';

interface VisitDetailsDialogProps {
  visit: Visit;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VisitDetailsDialog({ visit, open, onOpenChange }: VisitDetailsDialogProps) {
  const { data: inventory } = useInventory();
  const { data: physician, isLoading: isLoadingPhysician } = useUser(visit.doctorId);
  const [prescriptionChanges, setPrescriptionChanges] = useState<PrescriptionChange[]>([]);

  useEffect(() => {
    if (open) {
      console.log('Fetching prescription changes for visit:', visit.id);
      getVisitPrescriptionChanges(visit.id)
        .then(changes => {
          console.log('Received prescription changes:', changes);
          setPrescriptionChanges(changes);
        })
        .catch(error => {
          console.error('Error fetching prescription changes:', error);
        });
    }
  }, [visit.id, open]);

  const getItemById = (id: string): InventoryItem | undefined => {
    return inventory?.find((item: InventoryItem) => item.id === id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[80vh]">
        <DialogHeader>
          <DialogTitle>Visit Details</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-full pr-4">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{visit.title}</h2>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(visit.startTime), 'MMMM d, yyyy h:mm a')}
                  </p>
                  {isLoadingPhysician ? (
                    <p className="text-sm text-muted-foreground">Loading physician details...</p>
                  ) : physician ? (
                    <p className="text-sm text-muted-foreground">
                      Physician: {physician.title} {physician.firstName} {physician.lastName}
                      {physician.specialization && (
                        <span className="block text-xs">{physician.specialization}</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Physician details not found</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Badge variant="outline">{visit.type}</Badge>
                <Badge variant="outline">{visit.status}</Badge>
              </div>
            </div>

            {visit.vitals && (
              <Card>
                <CardHeader>
                  <CardTitle>Vitals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {visit.vitals.temperature && (
                      <div>
                        <p className="text-sm text-muted-foreground">Temperature</p>
                        <p className="font-medium">{visit.vitals.temperature}°C</p>
                      </div>
                    )}
                    {visit.vitals.bloodPressure && (
                      <div>
                        <p className="text-sm text-muted-foreground">Blood Pressure</p>
                        <p className="font-medium">{visit.vitals.bloodPressure}</p>
                      </div>
                    )}
                    {visit.vitals.heartRate && (
                      <div>
                        <p className="text-sm text-muted-foreground">Heart Rate</p>
                        <p className="font-medium">{visit.vitals.heartRate} bpm</p>
                      </div>
                    )}
                    {visit.vitals.respiratoryRate && (
                      <div>
                        <p className="text-sm text-muted-foreground">Respiratory Rate</p>
                        <p className="font-medium">{visit.vitals.respiratoryRate} breaths/min</p>
                      </div>
                    )}
                    {visit.vitals.oxygenSaturation && (
                      <div>
                        <p className="text-sm text-muted-foreground">Oxygen Saturation</p>
                        <p className="font-medium">{visit.vitals.oxygenSaturation}%</p>
                      </div>
                    )}
                    {visit.vitals.height && (
                      <div>
                        <p className="text-sm text-muted-foreground">Height</p>
                        <p className="font-medium">{visit.vitals.height} m</p>
                      </div>
                    )}
                    {visit.vitals.weight && (
                      <div>
                        <p className="text-sm text-muted-foreground">Weight</p>
                        <p className="font-medium">{visit.vitals.weight} kg</p>
                      </div>
                    )}
                    {visit.vitals.bmi && (
                      <div>
                        <p className="text-sm text-muted-foreground">BMI</p>
                        <p className="font-medium">{visit.vitals.bmi}</p>
                      </div>
                    )}
                    {visit.vitals.bloodSugar && (
                      <div>
                        <p className="text-sm text-muted-foreground">Blood Sugar</p>
                        <p className="font-medium">{visit.vitals.bloodSugar} mmol/L</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {visit.symptoms && visit.symptoms.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Symptoms</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {visit.symptoms.map((symptom, index) => (
                      <Badge key={index} variant="secondary">
                        {symptom}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {visit.diagnosis && (
              <Card>
                <CardHeader>
                  <CardTitle>Diagnosis</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap">{visit.diagnosis}</p>
                </CardContent>
              </Card>
            )}

            <div className="space-y-4">
              {/* Current Visit Prescriptions */}
              {visit.prescriptions && visit.prescriptions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Current Prescriptions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {visit.prescriptions.map((prescription, index) => (
                        <div key={index} className="border-b pb-4 last:border-b-0 last:pb-0">
                          <div className="grid gap-2">
                            <div>
                              <p className="font-medium">{prescription.medication}</p>
                              <p className="text-sm text-muted-foreground">
                                {prescription.dosage} - {prescription.frequency} for {prescription.duration}
                              </p>
                            </div>
                            {prescription.notes && (
                              <p className="text-sm text-muted-foreground">
                                {prescription.notes}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Prescription Changes Section */}
              {prescriptionChanges && prescriptionChanges.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Prescription Changes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {prescriptionChanges.map((change, index) => (
                        <p key={index} className="text-sm text-muted-foreground">
                          {change.medication} was {change.changeType} on{' '}
                          {format(new Date(change.timestamp), 'MMMM d, yyyy h:mm a')}
                        </p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Prescription Changes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">No prescription changes in this visit</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Inventory Usage Section */}
            {visit.inventoryUsage && visit.inventoryUsage.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Items Used</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {visit.inventoryUsage.map((usage, index) => {
                      const item = getItemById(usage.itemId);
                      if (!item) return null;

                      return (
                        <div key={index} className="border-b pb-4 last:border-b-0 last:pb-0">
                          <div className="grid gap-2">
                            <div>
                              <p className="font-medium">{item.name}</p>
                              <p className="text-sm text-muted-foreground">
                                Quantity: {usage.quantity} {item.unit}
                              </p>
                            </div>
                            {usage.notes && (
                              <p className="text-sm text-muted-foreground">
                                {usage.notes}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {visit.notes && (
              <Card>
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap">{visit.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
